import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Tree,
  Button,
  Input,
  Space,
  Tag,
  Alert,
  Statistic,
  Spin,
} from 'antd';
import { modal } from '@/utils/antdStatic';
import {
  MenuOutlined,
  ToolOutlined,
  SearchOutlined,
  SafetyOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import type { RolePermissionConfigProps, Permission } from '../types';
import { filterPermissionTree, calculatePermissionStats, flattenPermissionTree } from '../utils';

const { Search } = Input;

export const RolePermissionConfig: React.FC<RolePermissionConfigProps> = ({
  role,
  permissions,
  rolePermissions,
  loading = false,
  readonly = false,
  onSave,
  onCancel,
}) => {
  const [checkedKeys, setCheckedKeys] = useState<(string | number)[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // 初始化选中的keys
  useEffect(() => {
    setCheckedKeys(rolePermissions.checkedKeys);
    // 默认展开第一级
    const firstLevelKeys = permissions.map((p) => p.id);
    setExpandedKeys(firstLevelKeys);
  }, [rolePermissions, permissions]);

  // 转换权限数据为Tree组件需要的格式
  const treeData = useMemo((): DataNode[] => {
    const convertToTreeNode = (nodes: Permission[]): DataNode[] => {
      return nodes.map((node) => ({
        key: node.id,
        title: (
          <Space size="small">
            {node.type === 'menu' ? (
              <MenuOutlined style={{ color: '#722ed1' }} />
            ) : (
              <ToolOutlined style={{ color: '#fa8c16' }} />
            )}
            <span>{node.name}</span>
            <Tag
              color={node.type === 'menu' ? 'purple' : 'orange'}
              style={{ fontSize: 11, fontFamily: 'monospace' }}
            >
              {node.code}
            </Tag>
          </Space>
        ),
        children: node.children
          ? convertToTreeNode(node.children)
          : undefined,
        disabled: readonly,
      }));
    };

    // 如果有搜索关键词，过滤树
    const filteredPermissions = searchKeyword
      ? filterPermissionTree(permissions, searchKeyword)
      : permissions;

    return convertToTreeNode(filteredPermissions);
  }, [permissions, searchKeyword, readonly]);

  // 权限统计
  const stats = useMemo(() => {
    const allStats = calculatePermissionStats(permissions);
    const checkedPermissions = flattenPermissionTree(permissions).filter((p) =>
      checkedKeys.includes(p.id)
    );
    return {
      total: allStats.total,
      checked: checkedPermissions.length,
      menuCount: checkedPermissions.filter((p) => p.type === 'menu').length,
      actionCount: checkedPermissions.filter((p) => p.type === 'action')
        .length,
    };
  }, [permissions, checkedKeys]);

  const handleCheck = (checked: any) => {
    setCheckedKeys(checked);
    setHasChanges(true);
  };

  const handleExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue as (string | number)[]);
    setAutoExpandParent(false);
  };

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    if (value) {
      // 搜索时自动展开所有匹配的节点
      const allKeys = flattenPermissionTree(permissions).map((p) => p.id);
      setExpandedKeys(allKeys);
      setAutoExpandParent(true);
    } else {
      // 清空搜索时恢复第一级展开
      const firstLevelKeys = permissions.map((p) => p.id);
      setExpandedKeys(firstLevelKeys);
    }
  };

  const handleSelectAll = () => {
    const allKeys = flattenPermissionTree(permissions).map((p) => p.id);
    setCheckedKeys(allKeys);
    setHasChanges(true);
  };

  const handleClearAll = () => {
    setCheckedKeys([]);
    setHasChanges(true);
  };

  const handleExpandAll = () => {
    const allKeys = flattenPermissionTree(permissions).map((p) => p.id);
    setExpandedKeys(allKeys);
  };

  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  const handleSave = () => {
    if (readonly) return;

    modal.confirm({
      title: '确认保存',
      content: `确定要保存角色"${role.name}"的权限配置吗？`,
      onOk: async () => {
        await onSave({
          roleId: role.id,
          permissionIds: checkedKeys,
          checkedKeys: checkedKeys,
          halfCheckedKeys: [],
        });
        setHasChanges(false);
      },
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Spin spinning={loading}>
        <Row gutter={24}>
          {/* 左侧：角色信息 */}
          <Col span={8}>
            <Card
              title="角色信息"
              extra={
                role.type === 'system' ? (
                  <Tag color="blue">系统角色</Tag>
                ) : (
                  <Tag color="green">自定义</Tag>
                )
              }
            >
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Space>
                    {role.type === 'system' ? (
                      <SafetyOutlined
                        style={{ fontSize: 32, color: '#1890ff' }}
                      />
                    ) : (
                      <UserOutlined
                        style={{ fontSize: 32, color: '#52c41a' }}
                      />
                    )}
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 500 }}>
                        {role.name}
                      </div>
                      <Tag
                        color="blue"
                        style={{ fontFamily: 'monospace', marginTop: 4 }}
                      >
                        {role.code}
                      </Tag>
                    </div>
                  </Space>
                </div>

                {role.description && (
                  <div>
                    <div
                      style={{
                        color: '#999',
                        fontSize: 12,
                        marginBottom: 4,
                      }}
                    >
                      描述
                    </div>
                    <div>{role.description}</div>
                  </div>
                )}

                {readonly && (
                  <Alert
                    message="系统角色权限为只读"
                    description="系统角色的权限配置不可修改"
                    type="warning"
                    showIcon
                  />
                )}

                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="已选权限"
                      value={stats.checked}
                      suffix={`/ ${stats.total}`}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="菜单/操作"
                      value={stats.menuCount}
                      suffix={`/ ${stats.actionCount}`}
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                </Row>

                {!readonly && (
                  <Space style={{ width: '100%' }}>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleSave}
                      disabled={!hasChanges}
                      block
                    >
                      保存
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={onCancel}
                      block
                    >
                      取消
                    </Button>
                  </Space>
                )}
              </Space>
            </Card>
          </Col>

          {/* 右侧：权限树 */}
          <Col span={16}>
            <Card title="权限配置">
              <Space
                direction="vertical"
                size="middle"
                style={{ width: '100%' }}
              >
                {/* 搜索和操作栏 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Search
                    placeholder="搜索权限名称或代码"
                    allowClear
                    style={{ width: 300 }}
                    onSearch={handleSearch}
                    prefix={<SearchOutlined />}
                  />
                  <Space>
                    <Button size="small" onClick={handleExpandAll}>
                      展开全部
                    </Button>
                    <Button size="small" onClick={handleCollapseAll}>
                      折叠全部
                    </Button>
                    {!readonly && (
                      <>
                        <Button size="small" onClick={handleSelectAll}>
                          全选
                        </Button>
                        <Button size="small" onClick={handleClearAll}>
                          清空
                        </Button>
                      </>
                    )}
                  </Space>
                </div>

                {/* 权限树 */}
                <div
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                    padding: 16,
                    maxHeight: 600,
                    overflow: 'auto',
                  }}
                >
                  <Tree
                    checkable
                    checkStrictly={false}
                    expandedKeys={expandedKeys}
                    checkedKeys={checkedKeys}
                    onExpand={handleExpand}
                    onCheck={handleCheck}
                    treeData={treeData}
                    autoExpandParent={autoExpandParent}
                    height={560}
                  />
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};
